/**
 * one-stop/registry — the type switch, and the only one in the package.
 *
 * THE COVERAGE INVARIANT
 * `ONE_STOP_BLOCK_RENDERERS` is typed `satisfies Record<OneStopExperienceBlockType,
 * BlockRenderer>`. That single word does the work the docket asks for: adding a
 * type to the registry union without adding a component here is a COMPILE ERROR,
 * not a blank section someone finds in production. The Record also forbids the
 * reverse in practice — a key that is not a registry type will not typecheck —
 * so the map cannot drift in either direction.
 *
 * THE UNSUPPORTED PATH, AND WHY IT IS NOT THEORETICAL
 * An experience document is authorable with 65 types today: these 20 plus the 45
 * shipped canvas types (`text`, `hero_banner`, `root_page`, `email_*` …), which
 * are allowed by omission until a contract fix lands. So a real published page
 * can carry a block this package has no component for.
 *
 * The decision is made PURELY CLIENT-SIDE, from this map: a `block.type` that is
 * not a key of `ONE_STOP_BLOCK_RENDERERS` is unsupported. Resolver notes are NOT
 * consulted — they do not reach the wire today, and the note that exists fires
 * for config-only types like `search` and `context-banner`, so reading them would
 * report supported blocks as broken.
 *
 *   builder host → a visible card carrying {id, type, version}, so an admin learns
 *                  BEFORE publishing that this block will not appear.
 *   portal host  → nothing rendered, reported once, the rest of the list keeps
 *                  going. Never throws, never blanks the page.
 *
 * Arbitrary config must never crash a page either — which is why every config
 * read in this package narrows and defaults instead of asserting.
 */
import type { JSX, ReactNode } from "react";
import {
  AppointmentsBlock,
} from "./blocks-appointments";
import {
  ButtonBlock,
  FaqBlock,
  NoticeBlock,
  QuoteBlock,
  SocialBlock,
  SpacerBlock,
  StatsBlock,
} from "./blocks-content";
import { EventsBlock, FeaturedEventBlock } from "./blocks-events";
import {
  BookAppointmentBlock,
  JoinQueueBlock,
  RegisterEventBlock,
} from "./blocks-flow";
import { ContextBannerBlock, HelpContactBlock, IdentityPanelBlock, SearchBlock } from "./blocks-hero";
import { ChecklistBlock, DirectoryBlock, MyVisitsBlock } from "./blocks-lists";
import { QueuesBlock } from "./blocks-queues";
import { reportUnknown } from "./primitives";
import { uiStrings } from "./strings";
import { CANVAS_BLOCK_CONSTRAINTS, ONE_STOP_BLOCK_TYPES } from "@v0y4ge/api-contracts";
import type { BlockContext, BlockRenderer, OneStopBlock, OneStopExperienceBlockType } from "./types";
import { ONE_STOP_EXPERIENCE_BLOCK_TYPES, ONE_STOP_PENDING_BLOCK_TYPES, isOneStopExperienceBlockType } from "./types";

export const ONE_STOP_BLOCK_RENDERERS = {
  // live
  search: SearchBlock,
  "identity-panel": IdentityPanelBlock,
  "context-banner": ContextBannerBlock,
  queues: QueuesBlock,
  appointments: AppointmentsBlock,
  events: EventsBlock,
  // Pending contract type (see `ONE_STOP_PENDING_BLOCK_TYPES`): the arm ships
  // first so the backend's block-coverage guard can pass on the same ingest.
  "featured-event": FeaturedEventBlock,
  directory: DirectoryBlock,
  checklist: ChecklistBlock,
  "my-visits": MyVisitsBlock,
  // flow-entry
  "book-appointment": BookAppointmentBlock,
  "register-event": RegisterEventBlock,
  "join-queue": JoinQueueBlock,
  // content
  "help-contact": HelpContactBlock,
  notice: NoticeBlock,
  button: ButtonBlock,
  faq: FaqBlock,
  quote: QuoteBlock,
  stats: StatsBlock,
  social: SocialBlock,
  spacer: SpacerBlock,
} satisfies Record<OneStopExperienceBlockType, BlockRenderer>;

/* ── the other half of the coverage invariant ──────────────────────────
 *  `satisfies` above catches a type this package fails to render. It cannot
 *  catch the case that actually reaches production: the CONTRACT starting to
 *  admit a type into an `experience` document that this list has never heard
 *  of. That filter is `allowed_document_kinds`, and `CANVAS_BLOCK_CONSTRAINTS`
 *  is assembled at runtime, so the document kinds never enter the type system
 *  and no `Extract<…>` can read them (which is why `types.ts` still spells the
 *  list instead of deriving it).
 *
 *  So the same question is asked at module load, against the table itself. A
 *  drift in either direction is reported once, loudly, in the console — the
 *  builder's palette is driven by this table too, so a mismatch means an admin
 *  is being offered a block that renders as an Unsupported card, or is NOT
 *  being offered one that would render perfectly well. Reported, never thrown:
 *  a vocabulary disagreement must not blank a student's page.
 */
function reportRegistryDrift(): void {
  const admitted = ONE_STOP_BLOCK_TYPES.filter((type) =>
    CANVAS_BLOCK_CONSTRAINTS[type]?.allowed_document_kinds.includes("experience"),
  );
  const rendered: ReadonlySet<string> = new Set(ONE_STOP_EXPERIENCE_BLOCK_TYPES);
  const admittedSet: ReadonlySet<string> = new Set(admitted);
  const missing = admitted.filter((type) => !rendered.has(type));
  // A PENDING type is rendered here on purpose while the contract catches up; it
  // is not drift, so it is not reported as such.
  const pending: ReadonlySet<string> = new Set(ONE_STOP_PENDING_BLOCK_TYPES);
  const extra = ONE_STOP_EXPERIENCE_BLOCK_TYPES.filter((type) => !admittedSet.has(type) && !pending.has(type));
  if (missing.length === 0 && extra.length === 0) return;
  // eslint-disable-next-line no-console -- one line, once, and it is a contract fault
  console.error(
    "[one-stop] block vocabulary drift against CANVAS_BLOCK_CONSTRAINTS.",
    missing.length ? `Admitted into an experience document but not rendered here: ${missing.join(", ")}.` : "",
    extra.length ? `Rendered here but no longer admitted: ${extra.join(", ")}.` : "",
  );
}

reportRegistryDrift();

/* ── the unsupported card ──────────────────────────────────────────── */

export function UnsupportedBlockCard(props: {
  block: OneStopBlock;
  locale: string;
}): JSX.Element {
  const t = uiStrings(props.locale);
  return (
    <div className="lv-unsupported" role="note">
      <span className="lv-unsupported-title">{t("block.unsupported.title")}</span>
      <span className="lv-unsupported-body">{t("block.unsupported.body")}</span>
      <span className="lv-unsupported-meta">
        {props.block.type} · v{props.block.version} · {props.block.id}
      </span>
    </div>
  );
}

/* ── one block ────────────────────────────────────────────────────── */

export function renderBlock(block: OneStopBlock, ctx: BlockContext): ReactNode {
  if (isOneStopExperienceBlockType(block.type)) {
    const Renderer: BlockRenderer =
      ONE_STOP_BLOCK_RENDERERS[block.type as OneStopExperienceBlockType];
    return <Renderer key={block.id} block={block} ctx={ctx} />;
  }
  if (ctx.host === "builder") {
    return <UnsupportedBlockCard key={block.id} block={block} locale={ctx.locale} />;
  }
  reportUnknown("block", { id: block.id, type: block.type, version: block.version });
  return null;
}

/* ── the list ─────────────────────────────────────────────────────── */

/** ui-config — the three types the prototype composes into its hero band. */
const HERO_TYPES: ReadonlySet<string> = new Set(["context-banner", "search", "identity-panel"]);

/**
 * How many leading blocks belong in the hero.
 *
 * THE RULE, and it is a LAYOUT rule, not a content decision: the hero band is the
 * maximal PREFIX of the document made only of `context-banner` / `search` /
 * `identity-panel`, and it exists only when that prefix actually contains a
 * `search` or an `identity-panel`. The order is the document's, untouched;
 * nothing is reordered, hidden or promoted.
 *
 * That is what reproduces the prototype: `home` (banner, search, sign-in, …) gets
 * the maroon band with the search card and the sign-in panel side by side, while
 * `walk-in-waits`, `events` and `get-help` — which open with a list or a banner —
 * get the prototype's plain header-and-content layout, exactly as it has.
 */
function heroPrefixLength(blocks: readonly OneStopBlock[]): number {
  let end = 0;
  while (end < blocks.length && HERO_TYPES.has(blocks[end]!.type)) end += 1;
  const prefix = blocks.slice(0, end);
  const anchored = prefix.some((b) => b.type === "search" || b.type === "identity-panel");
  return anchored ? end : 0;
}

/**
 * Renders an ordered block array. THE type switch lives in `renderBlock` and
 * nowhere else; this component only decides where the hero band ends.
 *
 * Both hosts mount exactly this. `host` changes one thing and one thing only:
 * what an unrecognised type does.
 */
export function BlockList(props: {
  blocks: readonly OneStopBlock[];
  ctx: BlockContext;
  /** Rendered above the hero grid — the document's own subtitle. */
  heroEyebrow?: string | null;
}): JSX.Element {
  const { blocks, ctx } = props;
  const heroEnd = heroPrefixLength(blocks);
  const hero = blocks.slice(0, heroEnd);
  const body = blocks.slice(heroEnd);

  const banners = hero.filter((b) => b.type === "context-banner");
  const search = hero.find((b) => b.type === "search") ?? null;
  const identity = hero.find((b) => b.type === "identity-panel") ?? null;

  return (
    <>
      {heroEnd > 0 ? (
        <section className="lv-hero">
          {props.heroEyebrow ? <span className="lv-hero-eyebrow">{props.heroEyebrow}</span> : null}
          {banners.map((block) => renderBlock(block, ctx))}
          <div className={identity ? "lv-hero-grid" : "lv-hero-grid lv-solo"}>
            <div>{search ? renderBlock(search, ctx) : null}</div>
            {identity ? renderBlock(identity, ctx) : null}
          </div>
        </section>
      ) : null}
      {body.map((block) => renderBlock(block, ctx))}
    </>
  );
}
